import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Container } from '@/components/layout/Container';
import { Stack } from '@/components/layout/Stack';
import { Card, CardContent } from '@/components/ui/Card';
import { LoadingState } from '@/components/ui/Loading';
import { ErrorState } from '@/components/ui/ErrorState';
import { EmptyState } from '@/components/ui/EmptyState';
import { GoalForm } from '@/components/goals/GoalForm';
import { GoalsList } from '@/components/goals/GoalsList';
import { GoalFiltersComponent } from '@/components/goals/GoalFilters';
import { DeleteConfirmModal } from '@/components/shared/DeleteConfirmModal';
import { useGoalsStore } from '@/store/goalsStore';
import { useAspirationsStore } from '@/store/aspirationsStore';
import { useNavigationStore } from '@/store/navigationStore';
import { Goal, GoalFilters, GoalSort } from '@/types/goals';
import { Target } from 'lucide-react';
import { useEntityPage, createSortFn } from '@/hooks/useEntityPage';

// Default filter and sort configurations
const defaultFilters: GoalFilters = {};
const defaultSort: GoalSort = { field: 'priority', direction: 'asc' };

// Filter function for goals
function filterGoals(goals: Goal[], filters: GoalFilters): Goal[] {
  let filtered = [...goals];

  // Apply search filter
  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    filtered = filtered.filter(goal =>
      goal.title.toLowerCase().includes(searchLower) ||
      goal.description.toLowerCase().includes(searchLower) ||
      goal.smartCriteria.specific.toLowerCase().includes(searchLower) ||
      goal.smartCriteria.measurable.toLowerCase().includes(searchLower)
    );
  }

  // Apply status filter
  if (filters.status?.length) {
    filtered = filtered.filter(goal =>
      filters.status!.includes(goal.status)
    );
  }

  // Apply aspiration filter
  if (filters.aspirationId?.length) {
    filtered = filtered.filter(goal =>
      goal.aspirationId && filters.aspirationId!.includes(goal.aspirationId)
    );
  }

  // Apply overdue filter
  if (filters.isOverdue) {
    const now = new Date();
    filtered = filtered.filter(goal =>
      goal.deadline &&
      goal.deadline < now &&
      goal.status !== 'completed' &&
      goal.status !== 'cancelled'
    );
  }

  // Apply deadline filter
  if (filters.hasDeadline) {
    filtered = filtered.filter(goal => !!goal.deadline);
  }

  return filtered;
}

// Sort function using the helper
const sortGoals = createSortFn<Goal>();

export function Goals() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const {
    goals: rawGoals,
    isLoading,
    error,
    initializeGoals,
    deleteGoal,
    reorderGoals,
    getGoalsWithCalculatedProgress
  } = useGoalsStore();

  const { aspirations } = useAspirationsStore();
  const { setPageInfo } = useNavigationStore();

  // Track re-render trigger for completion updates
  const [completionUpdateTrigger, setCompletionUpdateTrigger] = React.useState(0);

  // Use calculated progress from actual list item completions
  const goals = React.useMemo(() => {
    return getGoalsWithCalculatedProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawGoals, completionUpdateTrigger]);

  // Parse URL params for initial filters
  const parseUrlFilters = React.useCallback((params: URLSearchParams): Partial<GoalFilters> => {
    const aspirationId = params.get('aspirationId');
    return aspirationId ? { aspirationId: [aspirationId] } : {};
  }, []);

  // Use the unified entity page hook
  const {
    filters,
    setFilters,
    sort,
    setSort,
    filteredItems: filteredGoals,
    clearFilters,
    isFormOpen,
    setIsFormOpen,
    editingId: editingGoalId,
    setEditingId: setEditingGoalId,
    deleteConfirm,
    setDeleteConfirm,
    openCreateForm,
    closeForm,
  } = useEntityPage<Goal, GoalFilters, GoalSort>({
    items: goals,
    initializeItems: initializeGoals,
    setPageInfo,
    pageTitle: 'Goals',
    actionLabel: 'New Goal',
    actionIcon: Target,
    defaultFilters,
    defaultSort,
    filterFn: filterGoals,
    sortFn: sortGoals,
    searchParams,
    parseUrlFilters,
  });

  // Force re-render when completion data changes
  React.useEffect(() => {
    const handleCompletionUpdate = () => {
      setCompletionUpdateTrigger(prev => prev + 1);
    };
    window.addEventListener('universal-completion-updated', handleCompletionUpdate);
    return () => {
      window.removeEventListener('universal-completion-updated', handleCompletionUpdate);
    };
  }, []);

  // Delete handler
  const handleDeleteGoal = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteGoal(deleteConfirm.id);
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Failed to delete goal:', err);
    }
  };

  // Reorder handler
  const handleReorderGoals = (reorderedGoals: Goal[]) => {
    console.log('🚀 Starting goal reorder operation...');
    reorderGoals(reorderedGoals);
  };

  // Check if exactly one aspiration is selected for drag and drop
  const isSingleAspirationSelected = filters.aspirationId?.length === 1;
  const shouldShowDragHandles = isSingleAspirationSelected;

  // Edit handler
  const openEditForm = (goal: Goal) => {
    setEditingGoalId(goal.id);
    setIsFormOpen(true);
  };

  // Navigation handler
  const handleGoalClick = (goal: Goal) => {
    navigate(`/lists?goalId=${goal.id}`);
  };

  // Filter change handler (wraps setFilters to match GoalFiltersComponent API)
  const handleFiltersChange = (newFilters: GoalFilters) => {
    setFilters(newFilters);
  };

  if (isLoading) {
    return (
      <Container size="full" className="py-6">
        <LoadingState title="Loading goals..." />
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="full" className="py-6">
        <ErrorState
          title="Failed to load goals"
          description={error}
          action={{
            label: "Try Again",
            onClick: initializeGoals
          }}
        />
      </Container>
    );
  }

  const hasGoals = goals.length > 0;

  // Get aspirations for the filter component
  const aspirationsForFilter = aspirations.map(asp => ({
    id: asp.id,
    title: asp.title
  }));

  // Calculate cascade info for delete confirmation
  const deleteConfirmCascadeInfo = deleteConfirm ? [
    { label: 'linked list', count: deleteConfirm.lists?.length || 0 }
  ] : [];

  return (
    <Container size="full" className="py-6">
      <Stack spacing="lg">

        {hasGoals ? (
          <Stack spacing="lg">
            <GoalFiltersComponent
              filters={filters}
              sort={sort}
              onFiltersChange={handleFiltersChange}
              onSortChange={setSort}
              onClearFilters={clearFilters}
              totalCount={goals.length}
              filteredCount={filteredGoals.length}
              aspirations={aspirationsForFilter}
            />

            {filteredGoals.length > 0 ? (
              <GoalsList
                goals={filteredGoals}
                onEdit={openEditForm}
                onDelete={setDeleteConfirm}
                onClick={handleGoalClick}
                onReorder={handleReorderGoals}
                showDragHandles={shouldShowDragHandles}
              />
            ) : (
              <Card>
                <CardContent className="py-12">
                  <EmptyState
                    title="No goals match your filters"
                    description="Try adjusting your search or filter criteria to see more results."
                    action={{
                      label: "Clear Filters",
                      onClick: clearFilters
                    }}
                  />
                </CardContent>
              </Card>
            )}
          </Stack>
        ) : (
          <Card>
            <CardContent className="py-12">
              <EmptyState
                icon={<Target className="h-12 w-12 text-muted-foreground" />}
                title="No goals yet"
                description="Start by creating your first SMART goal - a specific, measurable objective that moves you toward your aspirations."
                action={{
                  label: "Create Your First Goal",
                  onClick: openCreateForm
                }}
              />
            </CardContent>
          </Card>
        )}
      </Stack>

      {/* Create/Edit Form Modal */}
      <GoalForm
        isOpen={isFormOpen}
        onClose={closeForm}
        goalId={editingGoalId || undefined}
        isEditing={!!editingGoalId}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDeleteGoal}
        title="Delete Goal"
        itemName={deleteConfirm?.title || ''}
        itemType="goal"
        description="All progress data, milestones, and SMART criteria will be permanently deleted."
        cascadeInfo={deleteConfirmCascadeInfo}
      />
    </Container>
  );
}
